import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: t('home'), path: '/' },
    { label: t('about'), path: '/about' },
    { label: t('divisions'), path: '/divisions' },
    { label: t('projects'), path: '/projects' },
    { label: t('news'), path: '/news' },
    { label: t('certificates'), path: '/certificates' },
    { label: t('contact'), path: '/contact' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-[#141F48]/95 backdrop-blur-md shadow-lg' : 'bg-white/95 backdrop-blur-sm shadow-md'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img
              src={isScrolled ? "/assets/logos/almad-horizontal-white.png" : "/assets/logos/almad-horizontal-dark.png"}
              alt="ALMAD Contracting"
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                  location.pathname === link.path
                    ? isScrolled ? 'text-[#D3BC8D]' : 'text-[#D3BC8D]'
                    : isScrolled ? 'text-white hover:text-[#D3BC8D]' : 'text-[#141F48] hover:text-[#D3BC8D]'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Language Toggle & CTA */}
          <div className="hidden lg:flex items-center space-x-4">
            <button
              onClick={toggleLanguage}
              className={`flex items-center space-x-2 px-3 py-2 transition-colors duration-200 ${
                isScrolled ? 'text-white hover:text-[#D3BC8D]' : 'text-[#141F48] hover:text-[#D3BC8D]'
              }`}
            >
              <Globe className="w-4 h-4" />
              <span className="text-sm font-medium">{language === 'en' ? 'العربية' : 'English'}</span>
            </button>
            <a href="https://raci-procedures.emergent.host/auth" target="_blank" rel="noopener noreferrer">
              <Button className="bg-[#D3BC8D] text-[#141F48] hover:bg-[#c9b180] font-semibold">
                ALMAD PEP APP
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-2 hover:text-[#D3BC8D] transition-colors ${
              isScrolled ? 'text-white' : 'text-[#141F48]'
            }`}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-[#141F48] border-t border-white/10">
          <div className="px-4 py-4 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block px-4 py-2 text-base rounded-md transition-colors duration-200 ${
                  location.pathname === link.path
                    ? 'bg-[#D3BC8D]/10 text-[#D3BC8D]'
                    : 'text-white hover:bg-white/5'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <button
              onClick={toggleLanguage}
              className="flex items-center space-x-2 w-full px-4 py-2 text-white hover:bg-white/5 rounded-md transition-colors"
            >
              <Globe className="w-4 h-4" />
              <span>{language === 'en' ? 'العربية' : 'English'}</span>
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;